/**
 * Production Error Tracking Logger
 * 
 * PURPOSE: Persists user-facing errors to Supabase database for production monitoring
 * STORAGE: usage_logs table in Supabase
 * WHEN TO USE: Critical errors that need tracking and investigation (auth failures, API errors, data corruption)
 * 
 * This is DIFFERENT from structured-logger.ts which only logs to console for development debugging.
 * Both loggers serve different purposes and should coexist.
 * 
 * Usage:
 *   await errorLogger.log(error, { severity: 'error', additionalContext: { userId } });
 */
import { supabase } from "@/integrations/supabase/client";

import type { Database } from '@/integrations/supabase/types';

export interface ErrorLog {
  message: string;
  stack?: string;
  componentStack?: string;
  userAgent?: string;
  url?: string;
  timestamp: string;
  userId?: string;
  organizationId?: string;
  severity: 'error' | 'warning' | 'info';
  context?: Record<string, unknown>;
}

class ErrorLogger {
  private queue: ErrorLog[] = [];
  private flushInterval: number = 5000; // 5 seconds
  private maxQueueSize: number = 10;

  constructor() {
    // Flush queue periodically
    if (typeof window !== 'undefined') {
      setInterval(() => this.flush(), this.flushInterval);
    }
  }

  async log(error: Error | string, context?: {
    severity?: 'error' | 'warning' | 'info';
    componentStack?: string;
    additionalContext?: Record<string, unknown>;
  }) {
    try {
      const errorMessage = typeof error === 'string' ? error : error.message;
      const errorStack = typeof error === 'string' ? undefined : error.stack;

      // Get user info
      const { data: { session } } = await supabase.auth.getSession();
      let organizationId: string | undefined;

      if (session?.user) {
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('organization_id')
          .eq('user_id', session.user.id)
          .maybeSingle();
        
        organizationId = roleData?.organization_id;
      }

      const errorLog: ErrorLog = {
        message: errorMessage,
        stack: errorStack,
        componentStack: context?.componentStack,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        userId: session?.user?.id,
        organizationId,
        severity: context?.severity || 'error',
        context: context?.additionalContext,
      };

      // Add to queue
      this.queue.push(errorLog);

      // Console log in development
      if (import.meta.env.DEV) {
        console.error('[ErrorLogger]', errorLog);
      }

      // Flush if queue is full
      if (this.queue.length >= this.maxQueueSize) {
        await this.flush();
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Failed to log error:', err);
      }
    }
  }

  private async flush() {
    if (this.queue.length === 0) return;

    const logsToSend = [...this.queue];

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('organization_id')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (roleData?.organization_id) {
          const { error } = await supabase
            .from('usage_logs')
            .insert([{
              organization_id: roleData.organization_id,
              feature: 'error_log',
              quantity: logsToSend.length,
              metadata: { errors: logsToSend } as any, // Complex nested type - safe to cast
            }]);

          // ✅ Only clear queue after successful insert
          if (!error) {
            this.queue = this.queue.filter(log => !logsToSend.includes(log));
          } else {
            if (import.meta.env.DEV) {
              console.error('Failed to flush error logs, retaining in queue:', error);
            }
          }
        }
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Failed to flush error logs:', err);
      }
      // Don't clear queue on error - logs will retry
    }
  }

  // Synchronous flush using sendBeacon for reliable page unload
  flushSync() {
    if (this.queue.length === 0 || typeof navigator.sendBeacon !== 'function') {
      return;
    }

    try {
      const logsToSend = [...this.queue];
      
      // Use sendBeacon for guaranteed delivery on page unload
      const blob = new Blob([JSON.stringify({ errors: logsToSend })], {
        type: 'application/json'
      });
      
      const sent = navigator.sendBeacon(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/usage_logs`,
        blob
      );

      if (sent) {
        this.queue = [];
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Failed to send beacon:', err);
      }
    }
  }

  // Async flush for periodic intervals
  async forceFlush() {
    await this.flush();
  }
}

export const errorLogger = new ErrorLogger();

// Use sendBeacon for reliable flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    errorLogger.flushSync();
  });
}

// Helper function for try-catch blocks
export const withErrorLogging = async <T>(
  fn: () => Promise<T>,
  context?: {
    severity?: 'error' | 'warning' | 'info';
    additionalContext?: Record<string, unknown>;
  }
): Promise<T | null> => {
  try {
    return await fn();
  } catch (error) {
    await errorLogger.log(error as Error, context);
    throw error;
  }
};
